/**
 * Procedure detail extractors — `normalizeProcedureSteps` plus the
 * `getPlantProcedureDetail` mapper. Steps probing is the shared read
 * shape later LineOperation UI can reuse for inline expansion.
 */

import { extractNode } from '../graph-row'
import { compareByNameThenId } from '../sort'
import type {
  CypherReadResult,
  NormalizedProcedureStep,
  PlantGenericEntity,
  PlantLine,
  PlantPartRef,
  PlantProcedureAppliesTo,
  PlantProcedureDetail,
  PlantProcedureDetailNode,
  PlantProcedurePartRequirement,
  PlantProcedureSkillRequirement,
  PlantProductRef,
  PlantRoleRef,
  PlantSite
} from '../types'
import { extractPlantEdges } from './edges'
import { withDisplayName } from './equipment-relationships'

const STEP_NUMBER_KEYS = ['step_number', 'number', 'index', 'seq'] as const
/** Prefer authored prose keys; `task` covers STDW / time-study ingest rows. */
const INSTRUCTION_KEYS = ['instruction', 'text', 'description', 'title', 'task'] as const

function optionalNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value)
    return Number.isFinite(n) ? n : undefined
  }
  return undefined
}

function optionalBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined
}

function formatDetailValue(value: unknown): string {
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

/**
 * Probe common step key shapes into a stable list. Missing instruction
 * keys fall back to a stringified row so opaque ingest payloads still
 * render something readable.
 */
export function normalizeProcedureSteps(
  steps: Record<string, unknown>[] | null | undefined
): NormalizedProcedureStep[] {
  if (!Array.isArray(steps)) return []

  return steps.map((row, index) => {
    if (!row || typeof row !== 'object' || Array.isArray(row)) {
      return { instruction: formatDetailValue(row), details: [] }
    }

    let stepNumber: number | undefined
    for (const key of STEP_NUMBER_KEYS) {
      const n = optionalNumber(row[key])
      if (n !== undefined) {
        stepNumber = n
        break
      }
    }

    let instruction: string | undefined
    for (const key of INSTRUCTION_KEYS) {
      const candidate = row[key]
      if (typeof candidate === 'string' && candidate.length > 0) {
        instruction = candidate
        break
      }
    }

    if (!instruction) {
      return {
        ...(stepNumber !== undefined ? { stepNumber } : { stepNumber: index + 1 }),
        instruction: formatDetailValue(row),
        details: []
      }
    }

    const reserved = new Set<string>([...STEP_NUMBER_KEYS, ...INSTRUCTION_KEYS])
    const details: Array<{ key: string; value: string }> = []
    for (const [key, value] of Object.entries(row)) {
      if (reserved.has(key) || value === null || value === undefined) continue
      details.push({ key, value: formatDetailValue(value) })
    }

    return {
      stepNumber: stepNumber ?? index + 1,
      instruction,
      details
    }
  })
}

function extractAppliesTo(value: unknown): PlantProcedureAppliesTo[] {
  if (!Array.isArray(value)) return []
  const rows: PlantProcedureAppliesTo[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const row = item as Record<string, unknown>
    const peer = extractNode<PlantGenericEntity>(row['peer'])
    if (!peer) continue
    const peerLabels = Array.isArray(row['peerLabels'])
      ? row['peerLabels'].filter((label): label is string => typeof label === 'string')
      : []
    rows.push({ peer, peerLabels })
  }
  return rows.sort((a, b) => compareByNameThenId(a.peer, b.peer))
}

function extractPartRequirements(value: unknown): PlantProcedurePartRequirement[] {
  if (!Array.isArray(value)) return []
  const rows: PlantProcedurePartRequirement[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const row = item as Record<string, unknown>
    const part = extractNode<PlantPartRef>(row['part'])
    if (!part) continue
    const edge = extractPlantEdges([row['edge']])[0]
    const requirement: PlantProcedurePartRequirement = { part }
    const quantity = optionalNumber(edge?.props['quantity'])
    const isOptional = optionalBoolean(edge?.props['is_optional'])
    if (quantity !== undefined) requirement.quantity = quantity
    if (isOptional !== undefined) requirement.isOptional = isOptional
    rows.push(requirement)
  }
  return rows.sort((a, b) => compareByNameThenId(a.part, b.part))
}

function extractSkillRequirements(value: unknown): PlantProcedureSkillRequirement[] {
  if (!Array.isArray(value)) return []
  const rows: PlantProcedureSkillRequirement[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const row = item as Record<string, unknown>
    const role = extractNode<PlantRoleRef>(row['role'])
    if (!role) continue
    const props =
      row['props'] && typeof row['props'] === 'object' && !Array.isArray(row['props'])
        ? (row['props'] as Record<string, unknown>)
        : {}
    const skill: PlantProcedureSkillRequirement = { role }
    const minGrade = optionalNumber(props['min_grade'])
    const maxGrade = optionalNumber(props['max_grade'])
    if (minGrade !== undefined) skill.minGrade = minGrade
    if (maxGrade !== undefined) skill.maxGrade = maxGrade
    rows.push(skill)
  }
  return rows.sort((a, b) => compareByNameThenId(a.role, b.role))
}

function extractProducts(value: unknown): PlantProductRef[] {
  if (!Array.isArray(value)) return []
  return value
    .map((item) => extractNode<PlantProductRef>(item))
    .filter((node): node is PlantProductRef => node !== null)
    .sort(compareByNameThenId)
}

function extractBreadcrumbSubject(
  subject: unknown,
  labelsValue: unknown
): PlantProcedureAppliesTo | null {
  const peer = extractNode<PlantGenericEntity>(subject)
  if (!peer) return null
  const peerLabels = Array.isArray(labelsValue)
    ? labelsValue.filter((label): label is string => typeof label === 'string')
    : []
  return { peer, peerLabels }
}

export function mapPlantProcedureDetail(
  raw: CypherReadResult,
  procedureId: string
): PlantProcedureDetail {
  const row = raw.results[0] ?? {}
  const procedure =
    extractNode<PlantProcedureDetailNode>(withDisplayName(row['p'], ['title'])) ??
    ({
      id: procedureId,
      _id: procedureId,
      name: 'Unknown procedure'
    } satisfies PlantProcedureDetailNode)

  return {
    procedure,
    appliesTo: extractAppliesTo(row['appliesTo']),
    parts: extractPartRequirements(row['parts']),
    skills: extractSkillRequirements(row['skills']),
    products: extractProducts(row['products']),
    site: extractNode<PlantSite>(row['site']),
    line: extractNode<PlantLine>(row['line']),
    breadcrumbSubject: extractBreadcrumbSubject(
      row['breadcrumbSubject'],
      row['breadcrumbSubjectLabels']
    ),
    isStray: row['isStray'] === true
  }
}
